// useTheme — user-selectable app theme (Default ↔ Cyberpunk). Flips a single data-theme
// attribute on <html> (token-driven CSS re-skins instantly) AND is backed by a shared store so
// every consumer — ThemePicker, CyberStatus, CyberRain, etc. — re-renders together the moment the
// theme changes (a plain per-component useState would leave those stale until a reload). Persisted
// per-device in localStorage.
import { useCallback, useSyncExternalStore } from "react";

const KEY = "tmc.theme";
export const THEMES = [
  { id: "default", name: "TMC Default", blurb: "Clean TMC blue on dark" },
  { id: "cyberpunk", name: "Cyberpunk", blurb: "Neon HUD · animated" },
  { id: "deep", name: "Deep Space", blurb: "Observatory mission-control" },
  { id: "terra", name: "Terra Botanical", blurb: "Warm organic solarpunk" },
  { id: "aurora", name: "Aurora Glass", blurb: "Airy frosted premium" },
  { id: "arcade", name: "Arcade", blurb: "Neon-lit midnight arcade" },
  { id: "yuletide", name: "Yuletide", blurb: "Cozy Christmas workshop" },
  { id: "spooky", name: "Spooky", blurb: "Halloween midnight" },
  { id: "bttf", name: "Back to the Future", blurb: "Time circuits · 88 mph" },
];

// Browser-chrome / PWA bar colour per theme (the <meta name="theme-color"> tag). Each value is
// that theme's page backdrop so the mobile address bar / installed title bar matches the app —
// spooky gets its dark purple, cyberpunk near-black, the light themes their parchment/glass, etc.
// Keep in sync with the tiny inline map in index.html's pre-mount boot script.
const THEME_COLORS = {
  default: "#080f1a",
  cyberpunk: "#05070e",
  deep: "#070b14",
  terra: "#f2ebdc",
  aurora: "#eaf0fb",
  arcade: "#0b0518",
  yuletide: "#0b1d18",
  spooky: "#1c1030",
  bttf: "#060b1e",
};

function applyTheme(id) {
  if (id === "default") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = id;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLORS[id] || THEME_COLORS.default);
}

// --- shared store ---
let current = (() => { try { return localStorage.getItem(KEY) || "default"; } catch { return "default"; } })();
const subscribers = new Set();
applyTheme(current); // keep the DOM attribute in sync on module load (harmless re-apply of the boot script)

function setThemeGlobal(id) {
  if (id === current) return;
  current = id;
  try { localStorage.setItem(KEY, id); } catch { /* private mode — non-fatal */ }
  applyTheme(id);
  subscribers.forEach((fn) => fn());
}
function subscribe(fn) { subscribers.add(fn); return () => subscribers.delete(fn); }
const getSnapshot = () => current;

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setTheme = useCallback((id) => setThemeGlobal(id), []);
  return { theme, setTheme };
}
